import { ReactNode, useId } from "react";
import styles from "./Switch.module.css";

interface Props {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
}

/**
 * Switch — accessible toggle styled per mockup design-system.html L252-L265.
 *
 * Renders a `<button role="switch">` so that Space/Enter activation, focus
 * outlines and screen-reader semantics come for free from the platform.
 * Wraps the track in a `<span>` that also carries the optional label, so the
 * whole row can be styled as a single inline-flex unit.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  id,
  "aria-label": ariaLabel,
}: Props) {
  const reactId = useId();
  const fid = id ?? reactId;
  const labelId = label ? `${fid}-label` : undefined;

  const trackClass = [styles.track, checked ? styles.on : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={styles.switchWrap}>
      <button
        id={fid}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={!label ? ariaLabel : undefined}
        aria-labelledby={label ? labelId : undefined}
        disabled={disabled}
        className={trackClass}
        onClick={() => {
          if (disabled) return;
          onChange(!checked);
        }}
      >
        <span className={styles.knob} aria-hidden />
      </button>
      {label && (
        <span
          id={labelId}
          className={[styles.label, disabled ? styles.labelDisabled : ""]
            .filter(Boolean)
            .join(" ")}
        >
          {label}
        </span>
      )}
    </span>
  );
}
